/**
 * Generador de scripts de Failover
 * Migrado de Python (backend/server.py) a JavaScript
 */

import { 
  getInterfaceSetCommand,
  getBaseIp,
  getNetwork,
  getFormattedDate
} from '../utils/routeros.js';

import { validateFailoverConfig } from '../utils/validators.js';

/**
 * Genera script de Failover con respaldo automático de WANs
 * @param {object} config - Configuración completa
 * @returns {string} Script generado
 */
export function generateFailoverScript(config) {
  validateFailoverConfig(config);
  
  const lanCidr = config.router.subnetLan;
  const lanNetwork = getNetwork(lanCidr);
  const baseIp = getBaseIp(lanCidr);
  const lanIp = `${baseIp}.1`;
  const dnsServers = config.router.dnsServers.join(',');
  
  // Ordenar WANs por prioridad
  const sortedWans = [...config.wans].sort((a, b) => a.prioridad - b.prioridad);
  const numWans = sortedWans.length;
  
  let script = `# MikroTik Failover Script
# Generated: ${getFormattedDate()}
# Configuration: ${numWans} WANs with Automatic Failover

/system identity
set name="${config.router.nombre}"

/system clock
set time-zone-name=${config.router.zonaHoraria}

/interface bridge
add name=bridge-local

`;
  
  // Configure WAN interfaces
  script += "/interface ethernet\n";
  sortedWans.forEach((wan, index) => {
    const i = index + 1;
    script += `set [ find default-name=${wan.puerto} ] name=WAN${i}\n`;
  });
  script += "\n";
  
  // IP Pool for DHCP
  const dhcpStart = `${baseIp}.2`;
  const dhcpEnd = `${baseIp}.254`;
  script += `/ip pool
add name=dhcp_pool ranges=${dhcpStart}-${dhcpEnd}

/ip dhcp-server
add address-pool=dhcp_pool disabled=no interface=bridge-local name=dhcp1

/ip address
add address=${lanNetwork}/24 interface=bridge-local network=${lanNetwork}
`;
  
  // WAN IP addresses
  sortedWans.forEach((wan, index) => {
    const i = index + 1;
    if (wan.tipo === "fijo") {
      const wanNetwork = wan.ip.split('.').slice(0, 3).join('.') + '.0';
      script += `add address=${wan.ip}/24 interface=WAN${i} network=${wanNetwork}\n`;
    }
  });
  
  script += `
/ip dhcp-server network
add address=${lanCidr} dns-server=${dnsServers} gateway=${lanIp}

`;
  
  // DHCP clients for dynamic WANs
  const dhcpWans = sortedWans
    .map((wan, index) => ({ wan, i: index + 1 }))
    .filter(({ wan }) => wan.tipo === "dhcp");
  
  if (dhcpWans.length > 0) {
    script += "/ip dhcp-client\n";
    dhcpWans.forEach(({ i }) => {
      script += `add default-route-distance=0 dhcp-options=hostname,clientid interface=WAN${i}\n`;
    });
    script += "\n";
  }
  
  // DNS
  script += `/ip dns
set allow-remote-requests=yes servers=${dnsServers}

`;
  
  // Firewall NAT - all WANs
  script += "/ip firewall nat\n";
  for (let i = 1; i <= numWans; i++) {
    script += `add action=masquerade chain=srcnat out-interface=WAN${i}\n`;
  }
  script += "\n";
  
  // Routes with failover - ordered by priority (distance)
  script += "/ip route\n";
  sortedWans.forEach((wan, index) => {
    const i = index + 1;
    const gateway = wan.tipo === "fijo" 
      ? wan.gateway 
      : `${baseIp}.254`;
    
    // Distance = priority (1 for primary, 2 for backup1, 3 for backup2, etc.)
    const distance = wan.prioridad;
    const checkIp = wan.checkGateway;
    
    script += `add check-gateway=${checkIp} distance=${distance} gateway=${gateway} comment="WAN${i} - Priority ${wan.prioridad}"\n`;
  });
  
  script += `
/system routerboard settings
set protected-routerboot=disabled

# Failover is configured via route distance and check-gateway
# Lower distance = higher priority
# Primary WAN (distance 1) is used by default
# If primary fails (ping check), secondary WAN (distance 2) takes over
# When primary recovers, traffic automatically returns to it
`;
  
  // ============================================
  // BRIDGE PORT AL FINAL (para evitar cortes)
  // ============================================
  script += "\n/interface bridge port\n";
  const puertosLan = config.puertosLan || 4;
  for (let i = 1; i <= puertosLan; i++) {
    // Asumiendo WANs empiezan desde ether1, LAN empieza después de las WANs
    const lanPort = `ether${sortedWans.length + i}`;
    script += `add bridge=bridge-local interface=${lanPort}\n`;
  }
  
  return script;
}
